import { HttpClient, HttpHeaders, HttpRequest, HttpEventType, HttpResponse, HttpParams } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { catchError, filter, map, timeout } from 'rxjs/operators';
import { IErrorStatus, IMap } from '../../models/interfaces';
import { IHttpStrategy } from './i-http.strategy';

const DEFAULT_TIMEOUT_MS = 30000;

export class UploadEvent {
  public constructor(
    public type: string,
    public sentBytes?: number,
    public totalBytes?: number,
  ) {}
}

export abstract class BaseHttpService {
  private readonly httpStrategy: IHttpStrategy;

  protected get http(): IHttpStrategy {
    return this.httpStrategy;
  }

  public constructor(
    http: HttpClient,
    private readonly baseUrl: string,
    private readonly getLoggingContext: () => string,
    private readonly getChangeHistoryHeaders: () => object = (): object => ({}),
    private readonly defaultTimeoutMs: number = DEFAULT_TIMEOUT_MS,
  ) {
    this.httpStrategy = http as IHttpStrategy;
  }

  public getUrl(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  public get(path: string, params: any = {}, customHeaders: IMap<string> = {}): Observable<any> {
    return this.http
      .get(`${this.baseUrl}${path}`, {
        headers: this.setHeaders(customHeaders),
        params,
        withCredentials: true,
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  public getResponse<TResponse>(path: string, params: any = {}): Observable<HttpResponse<TResponse>> {
    return this.http
      .get(`${this.baseUrl}${path}`, {
        headers: this.setHeaders(),
        params,
        withCredentials: true,
        observe: 'response',
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  public patch(path: string, body: object = {}, params: any = {}, baseUrl = this.baseUrl): Observable<any> {
    return this.http
      .patch(`${baseUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders(),
        withCredentials: true,
        params,
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  public put(path: string, body: object = {}, params: any = {}, baseUrl = this.baseUrl): Observable<any> {
    return this.http
      .put(`${baseUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders(),
        withCredentials: true,
        params,
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  /**
   * Performs a PUT request with custom headers. Use only for special cases (e.g., feature usage tracking).
   */
  public putWithHeaders(
    path: string,
    body: object = {},
    customHeaders: IMap<string> = {},
    params: any = {},
    baseUrl = this.baseUrl,
  ): Observable<any> {
    return this.http
      .put(`${baseUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders(customHeaders),
        withCredentials: true,
        params,
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  /**
   * Performs a PUT request with custom headers and returns the full response. Use when response headers (e.g. ETag) are needed.
   */
  public putWithHeadersResponse<TResponse>(
    path: string,
    body: object = {},
    customHeaders: IMap<string> = {},
    params: any = {},
  ): Observable<HttpResponse<TResponse>> {
    return this.http
      .put(`${this.baseUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders(customHeaders),
        withCredentials: true,
        params,
        observe: 'response',
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  public post(path: string, body: object = {}, customHeaders: IMap<string> = {}, params: any = {}): Observable<any> {
    return this.http
      .post(`${this.baseUrl}${path}`, body && JSON.stringify(body), {
        headers: this.setHeaders(customHeaders),
        withCredentials: true,
        params,
      })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  public postFormData<T = any>(path: string, formData: FormData, params: any = {}): Observable<T> {
    return this.http
      .request(
        new HttpRequest('POST', `${this.baseUrl}${path}`, formData, {
          params: params ? this.createHttpParams(params) : undefined,
          withCredentials: true,
        }),
      )
      .pipe(
        filter((event): event is HttpResponse<T> => event instanceof HttpResponse),
        map((event) => event.body),
        this.applyTimeout(100000),
        catchError(this.formatErrors),
      );
  }

  public delete(path: string, params: any = {}): Observable<any> {
    return this.http
      .delete(`${this.baseUrl}${path}`, { headers: this.setHeaders(), withCredentials: true, params })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  /**
   * Performs a DELETE request with custom headers. Use only for special cases (e.g. If-Match concurrency headers).
   */
  public deleteWithHeaders(path: string, customHeaders: IMap<string> = {}, params: any = {}): Observable<any> {
    return this.http
      .delete(`${this.baseUrl}${path}`, { headers: this.setHeaders(customHeaders), withCredentials: true, params })
      .pipe(this.applyTimeout(), catchError(this.formatErrors));
  }

  public download(path, params: any = {}): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}${path}`, {
        params,
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(this.applyTimeout(100000), catchError(this.formatErrors));
  }

  public postDownload(path: string, body: object = {}): Observable<Blob> {
    return this.http
      .post(`${this.baseUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders(),
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(this.applyTimeout(100000), catchError(this.formatErrors));
  }

  public postDownloadResponse(path: string, body: object = {}): Observable<HttpResponse<Blob>> {
    return this.http
      .post(`${this.baseUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders(),
        responseType: 'blob',
        withCredentials: true,
        observe: 'response',
      })
      .pipe(this.applyTimeout(100000), catchError(this.formatErrors));
  }

  public upload(path: string, data: any, params?: any, headers?: HttpHeaders): Observable<UploadEvent> {
    return this.http
      .request(
        new HttpRequest('PUT', `${this.baseUrl}${path}`, data, {
          params: params ? this.createHttpParams(params) : undefined,
          reportProgress: true,
          headers,
        }),
      )
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return new UploadEvent('INPROGRESS', event.loaded, event.total);
          } else if (event instanceof HttpResponse) {
            return new UploadEvent('COMPLETED');
          }
        }),
        this.applyTimeout(100000),
        catchError(this.formatErrors),
      );
  }

  public formatErrors(response: any) {
    const errorObj = {
      url: response.url,
      httpStatus: response.status,
      httpStatusMessage: response.statusText,
      error:
        response.error &&
        (response.headers && response.headers.get('content-type') === 'application/json' ? JSON.parse(response.error) : response.error),
    } as IErrorStatus;

    return throwError(errorObj);
  }

  private applyTimeout<T>(ms: number = this.defaultTimeoutMs): MonoTypeOperatorFunction<T> {
    return ms > 0 ? timeout<T>(ms) : (source): Observable<T> => source;
  }

  private setHeaders(customHeaders: IMap<string> = {}): HttpHeaders {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
      'If-Modified-Since': '0',
      ...customHeaders,
    };

    const loggingContext = this.getLoggingContext();
    if (loggingContext) {
      headers['Nimbus-Logging-Context'] = this.getLoggingContext();
    }

    const changeHistoryHeaders = this.getChangeHistoryHeaders();
    if (changeHistoryHeaders) {
      Object.keys(changeHistoryHeaders).forEach((key) => {
        if (changeHistoryHeaders[key]) {
          headers[key] = changeHistoryHeaders[key];
        }
      });
    }

    return new HttpHeaders(headers);
  }

  private createHttpParams(params: { [key: string]: string | string[] }): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach((key) => {
      const item = params[key];
      if (typeof item === 'string') {
        httpParams = httpParams.append(key, item);
      } else {
        item.forEach((val) => {
          httpParams = httpParams.append(key, val);
        });
      }
    });

    return httpParams;
  }
}
